import { Mediator, payload } from "modular-openscriptjs";
import { gc, dc, uc } from "../ojs.contexts";
import { api, STRINGS } from "../declarations";
import {
    deleteFromStorage,
    empty,
    getStore,
    inform,
    refreshOtpVerifiedAt,
    saveUser,
    store,
} from "../helpers";
import { loadGoogleMaps } from "../google-maps";
import { $e } from "../ojs.events";

export default class System extends Mediator {
    $$system = {
        booted: async () => {
            await this.loadAppConfigs();

            loadGoogleMaps(import.meta.env.VITE_GOOGLE_MAPS_KEY);

            gc.appLoaded.value = true;
            gc.appLoadedAt.value = (new Date()).getTime();

            this.send($e.app.loaded);
            this.fetchNotificationCount();
        },

        needs: {
            configReload: async () => {
                await this.loadAppConfigs();
                gc.appConfigReloaded.value = true;
                gc.appLoadedAt.value = (new Date()).getTime();

                this.send($e.app.reloaded);
                this.fetchNotificationCount();
            },
            notificationCount: () => this.fetchNotificationCount(),
        },

        has: {
            notification: () => this.fetchNotificationCount(),
        },
    };

    async fetchNotificationCount() {
        if (!gc.auth.value || !gc.user.value?.id) return;

        try {
            const res = await api.get(
                `user/${gc.user.value.id}/notifications/count`,
            );
            if (res && res.data) {
                dc.notificationsCount.value = res.data;
            }
        } catch (e) {
            console.error("Failed to load notification counts", e);
        }
    }

    async loadAppConfigs() {
        let res = await api.get("deliverer/configs");

        if (!res) {
            inform(
                "Oops! We could not open the app. Please try again.",
                STRINGS.WARNING,
                5000,
                false,
            );

            return;
        }

        let configs = res.data;

        for (let key in configs) {
            window[key] = configs[key];
        }

        gc.user.value = window.currentUser;
        gc.auth.value = window.isLoggedIn;

        // Re-scope the OTP grace period to whichever user this config
        // load resolved to — covers boot, config reloads after
        // login/logout, and any other account switch.
        await refreshOtpVerifiedAt(window.currentUser?.id);

        await saveUser(window.currentUser);
        uc.settings.value = window.userSettings;

        await this.refreshLocalStorage(configs);
    }

    async refreshLocalStorage(configs) {
        await saveUser(configs.currentUser);
        await store("delivery_companies", configs.companies);
        await store("user_settings", configs.userSettings);
        let currentCompany = await getStore("current_delivery_company");

        if (empty(configs.companies)) {
            await deleteFromStorage("current_delivery_company");
            await deleteFromStorage("delivery_companies");
            delete window.currentCompany;
            delete window.currentDeliveryCompany;
            delete window.deliveryCompanies;
            dc.deliveryCompanies.value = [];
        } else {
            dc.deliveryCompanies.value = configs.companies;

            if (currentCompany) {
                let company = configs.companies.find(
                    (c) => c.id == currentCompany.id,
                );

                if (empty(company)) {
                    currentCompany = configs.companies[0] ?? null;
                    await store("current_delivery_company", currentCompany);
                    window.currentCompany = currentCompany;
                    window.currentDeliveryCompany = currentCompany;
                } else {
                    window.currentCompany = company;
                    window.currentDeliveryCompany = company;
                    await store("current_delivery_company", company);
                }
            }
        }
    }
}
